import { PigeonsService } from './pigeons.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { HourClassPipe } from './pipes/hour-class.pipe';
import { IndexClassPipe } from './pipes/index-class.pipe';
import { IsPopupVisiblePipe } from './pipes/is-popup-visible.pipe';

import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  model,
  OnInit,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  hourClass,
  indexClass,
  isHourInRange,
  isPopupVisible,
} from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import { TippyInstance } from '@ngneat/helipopper/config';
import moment from 'moment';
import { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatButtonModule,
  MatIconModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  DecimalPipe,
  HourClassPipe,
  IndexClassPipe,
  IsPopupVisiblePipe,
  IsNilPipe,
];
export const DIRECTIVES = [TippyDirective];

@Component({
  selector: 'app-pigeons-matrix',
  templateUrl: './pigeons-matrix.component.html',
  styleUrls: ['./pigeons-matrix.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class PigeonsMatrixComponent implements OnInit {
  private injector = inject(Injector);
  pigeonsService = inject(PigeonsService);

  tooltips = viewChildren<TippyInstance>('tooltips');

  timeSteps = input<Array<number>>();
  selectedTimeStep = input<number>();
  stations = input<Array<any>>([]);
  mosData = input<{}>({});
  dtgIndex = input<number>();
  day = input<Moment>();
  stationTime = input<any>();
  selectedStation = model<number>();
  selectedStationIndex = input<number>();
  modelRun = input<Moment>();

  matrixClick = output<any>();

  utcOffset = 0;
  popupData = signal([]);
  matrixArray = signal({});
  popupVisible = signal({});

  constructor() {
    this.listenToStationsChange();
    this.listenToModelRunChanges();
  }

  ngOnInit(): void {
    this.utcOffset = moment().utcOffset() / 60;
  }

  onHourClick(hour): void {
    const selectedStation = this.selectedStation();
    const stationIndex = this.stations().findIndex(
      s => s.stationId === selectedStation
    );
    const stationHour = hour + stationIndex;
    this.matrixClick.emit({
      hour: stationHour,
      station: selectedStation,
      stationIndex,
    });
  }

  showTooltipForCell(stationId: number, hour: number, show: boolean) {
    this.popupVisible.set({});
    const currentPopupVisible = {};
    for (const element of this.tooltips()) {
      if (element.data().hour === hour) {
        if (show && element.data().station.stationId === stationId) {
          currentPopupVisible[hour] = stationId;
          element.show();
        } else {
          element.hide();
        }
      }
    }
    this.popupVisible.set({ ...currentPopupVisible });
  }

  onMatrixClick(hour: number, station: number, stationIndex: number): void {
    this.popupData.set([]);
    this.matrixClick.emit({ hour, station, stationIndex });
    this.onMatrixDblClick(hour, station, stationIndex);
  }

  onMatrixDblClick(
    hour: number,
    stationId: number,
    stationIndex: number
  ): void {
    const parameters = this.pigeonsService.parameterInfo;
    this.popupData.set([]);

    const mosData = this.mosData();
    if (isNil(mosData[stationId])) return;

    const mosIndex = this.pigeonsService.mosIndex(stationId, hour, this.day());
    const dataset = mosData[stationId][mosIndex];

    const popupItems = Object.keys(parameters).map(parameterKey => {
      const paramInfo = parameters[parameterKey];
      const parameter = paramInfo.name;

      let value = 0;
      const datasetValue = dataset?.[parameter];
      if (!isNil(datasetValue)) {
        value = dataset?.[parameter];
      } else if (parameter.indexOf('|') > 0) {
        const params = parameter.split('|');
        value = Math.max(dataset?.[params[0]], dataset?.[params[1]]);
      }

      return {
        icon: paramInfo.icon,
        value,
        parameter: parameterKey,
        color: this.pigeonsService.colorMapping(parameter, value),
      };
    });

    this.popupData.set(popupItems);
    this.setPopupVisible(hour, stationId, true);
    this.showTooltipForCell(stationId, hour, true);
  }

  private isPopupVisible(hour, stationId): boolean {
    return isPopupVisible(hour, stationId, this.popupVisible());
  }

  private indexClass(
    stationIndex: number,
    stationId,
    parameter: string,
    hour
  ): string {
    return indexClass(
      stationIndex,
      stationId,
      parameter,
      hour,
      this.day(),
      false,
      this.matrixArray(),
      this.mosData(),
      this.stationTime(),
      this.selectedTimeStep(),
      this.selectedStationIndex(),
      this.pigeonsService.day(),
      this.pigeonsService.mosData()
    );
  }

  private isHourInRange(hour: number): boolean {
    return isHourInRange(hour, this.stations(), this.stationTime());
  }

  private hourClass(hour: number): string {
    return hourClass(
      hour,
      this.selectedTimeStep(),
      this.stations(),
      this.stationTime()
    );
  }

  private setPopupVisible(
    hour: number,
    stationId: number,
    value: boolean
  ): void {
    this.popupVisible.set({});
    if (value) {
      this.popupVisible[hour] = value;
    }
  }

  private listenToStationsChange() {
    effect(
      () => {
        const stations = this.stations();

        untracked(() => {
          if (stations && stations.length > 0) {
            this.matrixArray.set({});
            const selectedStation = this.selectedStation();
            if (
              !selectedStation ||
              stations.filter(s => s.stationId === selectedStation).length < 1
            ) {
              this.selectedStation.set(stations[0].stationId);
            }
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToModelRunChanges() {
    effect(
      () => {
        this.modelRun();

        untracked(() => {
          this.matrixArray.set({});
        });
      },
      { injector: this.injector }
    );
  }
}
